import { useState } from "react";
import { trpc } from "../lib/trpc";
import {
  Shield, Plus, Eye, EyeOff, Trash2, Edit3, Save, X,
  Key, Database, GitBranch, Cloud, Zap, ToggleLeft, ToggleRight,
  Download, Lock, CheckCircle2, AlertCircle
} from "lucide-react";

const CATEGORY_CONFIG: Record<string, { label: string; icon: typeof Key; color: string }> = {
  api_key: { label: "API Key", icon: Key, color: "text-blue-400" },
  token: { label: "Token", icon: GitBranch, color: "text-success" },
  database: { label: "Database", icon: Database, color: "text-purple-400" },
  oauth: { label: "OAuth", icon: Lock, color: "text-yellow-400" },
  webhook: { label: "Webhook", icon: Zap, color: "text-orange-400" },
  custom: { label: "Custom", icon: Cloud, color: "text-gray-400" },
};

const SERVICE_LABELS: Record<string, string> = {
  voyage_ai: "Voyage AI",
  github: "GitHub",
  railway: "Railway",
  yos_brain_db: "YOS Brain DB",
  manus: "Manus API",
  llm_gateway: "LLM Gateway",
  openai: "OpenAI",
  cloudflare: "Cloudflare",
  stripe: "Stripe",
};

export function Vault() {
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [revealedIds, setRevealedIds] = useState<Set<string>>(new Set());
  const [formData, setFormData] = useState({
    key: "",
    value: "",
    category: "api_key" as string,
    description: "",
    service: "",
  });

  const vaultEntries = trpc.vault.list.useQuery();
  const upsertMutation = trpc.vault.upsert.useMutation({
    onSuccess: () => {
      vaultEntries.refetch();
      resetForm();
    },
  });
  const deleteMutation = trpc.vault.delete.useMutation({
    onSuccess: () => vaultEntries.refetch(),
  });
  const toggleMutation = trpc.vault.toggleActive.useMutation({
    onSuccess: () => vaultEntries.refetch(),
  });
  const importYOSMutation = trpc.vault.importFromYOS.useMutation({
    onSuccess: (data) => {
      vaultEntries.refetch();
      alert(`Importado: ${data.imported} credenciais do YOS`);
    },
  });

  function resetForm() {
    setShowForm(false);
    setEditingId(null);
    setFormData({ key: "", value: "", category: "api_key", description: "", service: "" });
  }

  function startEdit(entry: any) {
    setEditingId(entry.id);
    setFormData({
      key: entry.key,
      value: "",
      category: entry.category,
      description: entry.description || "",
      service: entry.service || "",
    });
    setShowForm(true);
  }

  function toggleReveal(id: string) {
    setRevealedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  const entries = vaultEntries.data || [];
  const activeCount = entries.filter((e) => e.isActive).length;

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500 to-blue-500 flex items-center justify-center">
            <Shield className="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-foreground">Vault</h1>
            <p className="text-sm text-muted-foreground">
              Gerenciamento seguro de credenciais e tokens
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => importYOSMutation.mutate()}
            disabled={importYOSMutation.isPending}
            className="flex items-center gap-2 px-3 py-2 rounded-lg bg-success/10 text-success hover:bg-success/20 border border-success/20 text-sm transition-colors"
          >
            <Download className="w-4 h-4" />
            {importYOSMutation.isPending ? "Importando..." : "Importar YOS"}
          </button>
          <button
            onClick={() => { resetForm(); setShowForm(true); }}
            className="flex items-center gap-2 px-3 py-2 rounded-lg bg-primary text-primary-foreground hover:opacity-90 text-sm transition-colors"
          >
            <Plus className="w-4 h-4" />
            Nova Credencial
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-card border border-border rounded-xl p-4">
          <div className="text-sm text-muted-foreground">Total</div>
          <div className="text-2xl font-bold text-foreground">{entries.length}</div>
        </div>
        <div className="bg-card border border-border rounded-xl p-4">
          <div className="text-sm text-muted-foreground">Ativas</div>
          <div className="text-2xl font-bold text-success">{activeCount}</div>
        </div>
        <div className="bg-card border border-border rounded-xl p-4">
          <div className="text-sm text-muted-foreground">Inativas</div>
          <div className="text-2xl font-bold text-yellow-400">{entries.length - activeCount}</div>
        </div>
      </div>

      {/* Form */}
      {showForm && (
        <div className="bg-card border border-border rounded-xl p-6 space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-foreground">
              {editingId ? "Editar Credencial" : "Nova Credencial"}
            </h3>
            <button onClick={resetForm} className="text-muted-foreground hover:text-foreground">
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-muted-foreground mb-1">Chave (identificador)</label>
              <input
                type="text"
                value={formData.key}
                onChange={(e) => setFormData({ ...formData, key: e.target.value.toUpperCase().replace(/\s/g, "_") })}
                placeholder="VOYAGE_API_KEY"
                disabled={!!editingId}
                className="w-full px-3 py-2 rounded-lg bg-background border border-border text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/50 disabled:opacity-50"
              />
            </div>
            <div>
              <label className="block text-sm text-muted-foreground mb-1">Categoria</label>
              <select
                value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                className="w-full px-3 py-2 rounded-lg bg-background border border-border text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
              >
                {Object.entries(CATEGORY_CONFIG).map(([key, cfg]) => (
                  <option key={key} value={key}>{cfg.label}</option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm text-muted-foreground mb-1">
              Valor {editingId && "(deixe vazio para manter o atual)"}
            </label>
            <input
              type="password"
              value={formData.value}
              onChange={(e) => setFormData({ ...formData, value: e.target.value })}
              placeholder={editingId ? "••••••••" : "sk-..."}
              className="w-full px-3 py-2 rounded-lg bg-background border border-border text-foreground text-sm font-mono focus:outline-none focus:ring-2 focus:ring-primary/50"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-muted-foreground mb-1">Serviço</label>
              <select
                value={formData.service}
                onChange={(e) => setFormData({ ...formData, service: e.target.value })}
                className="w-full px-3 py-2 rounded-lg bg-background border border-border text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
              >
                <option value="">Selecionar...</option>
                {Object.entries(SERVICE_LABELS).map(([key, label]) => (
                  <option key={key} value={key}>{label}</option>
                ))}
                <option value="other">Outro</option>
              </select>
            </div>
            <div>
              <label className="block text-sm text-muted-foreground mb-1">Descrição</label>
              <input
                type="text"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Descrição da credencial..."
                className="w-full px-3 py-2 rounded-lg bg-background border border-border text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
              />
            </div>
          </div>

          <div className="flex justify-end gap-2">
            <button onClick={resetForm} className="px-4 py-2 rounded-lg text-sm text-muted-foreground hover:text-foreground transition-colors">
              Cancelar
            </button>
            <button
              onClick={() => {
                if (!formData.key || (!formData.value && !editingId)) return;
                upsertMutation.mutate({
                  key: formData.key,
                  value: formData.value || "unchanged",
                  category: formData.category as any,
                  description: formData.description || undefined,
                  service: formData.service || undefined,
                });
              }}
              disabled={upsertMutation.isPending}
              className="flex items-center gap-2 px-4 py-2 rounded-lg bg-primary text-primary-foreground text-sm hover:opacity-90 transition-colors"
            >
              <Save className="w-4 h-4" />
              {upsertMutation.isPending ? "Salvando..." : "Salvar"}
            </button>
          </div>
        </div>
      )}

      {/* Entries List */}
      <div className="space-y-3">
        {entries.length === 0 ? (
          <div className="bg-card border border-border rounded-xl p-12 text-center">
            <Shield className="w-12 h-12 text-muted-foreground mx-auto mb-4 opacity-50" />
            <h3 className="text-lg font-semibold text-foreground mb-2">Vault Vazio</h3>
            <p className="text-sm text-muted-foreground mb-4">
              Adicione credenciais manualmente ou importe do YOS Brain.
            </p>
            <button
              onClick={() => importYOSMutation.mutate()}
              className="px-4 py-2 rounded-lg bg-success/10 text-success hover:bg-success/20 border border-success/20 text-sm transition-colors"
            >
              <Download className="w-4 h-4 inline mr-2" />
              Importar do YOS
            </button>
          </div>
        ) : (
          entries.map((entry) => {
            const catConfig = CATEGORY_CONFIG[entry.category] || CATEGORY_CONFIG.custom;
            const CatIcon = catConfig.icon;
            const isRevealed = revealedIds.has(entry.id);

            return (
              <div
                key={entry.id}
                className={`bg-card border rounded-xl p-4 transition-colors ${
                  entry.isActive ? "border-border" : "border-yellow-500/30 opacity-60"
                }`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <div className={`w-9 h-9 rounded-lg bg-background flex items-center justify-center ${catConfig.color}`}>
                      <CatIcon className="w-4 h-4" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-sm font-semibold text-foreground">{entry.key}</span>
                        {entry.service && (
                          <span className="text-xs px-2 py-0.5 rounded-full bg-primary/10 text-primary">
                            {SERVICE_LABELS[entry.service] || entry.service}
                          </span>
                        )}
                        {entry.isActive ? (
                          <CheckCircle2 className="w-3.5 h-3.5 text-success" />
                        ) : (
                          <AlertCircle className="w-3.5 h-3.5 text-yellow-400" />
                        )}
                      </div>
                      <div className="flex items-center gap-2 mt-0.5">
                        <span className="text-xs text-muted-foreground">{catConfig.label}</span>
                        {entry.description && (
                          <>
                            <span className="text-xs text-muted-foreground">·</span>
                            <span className="text-xs text-muted-foreground truncate">{entry.description}</span>
                          </>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    {/* Masked/revealed value */}
                    <span className="font-mono text-xs text-muted-foreground bg-background px-2 py-1 rounded">
                      {isRevealed ? entry.maskedValue : "••••••••"}
                    </span>

                    {/* Actions */}
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => toggleReveal(entry.id)}
                        className="p-1.5 rounded-lg hover:bg-background text-muted-foreground hover:text-foreground transition-colors"
                        title={isRevealed ? "Ocultar" : "Revelar parcial"}
                      >
                        {isRevealed ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                      <button
                        onClick={() => toggleMutation.mutate({ id: entry.id })}
                        className="p-1.5 rounded-lg hover:bg-background text-muted-foreground hover:text-foreground transition-colors"
                        title={entry.isActive ? "Desativar" : "Ativar"}
                      >
                        {entry.isActive ? (
                          <ToggleRight className="w-4 h-4 text-success" />
                        ) : (
                          <ToggleLeft className="w-4 h-4 text-yellow-400" />
                        )}
                      </button>
                      <button
                        onClick={() => startEdit(entry)}
                        className="p-1.5 rounded-lg hover:bg-background text-muted-foreground hover:text-foreground transition-colors"
                        title="Editar"
                      >
                        <Edit3 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => {
                          if (confirm(`Excluir credencial "${entry.key}"?`)) {
                            deleteMutation.mutate({ id: entry.id });
                          }
                        }}
                        className="p-1.5 rounded-lg hover:bg-red-500/10 text-muted-foreground hover:text-red-400 transition-colors"
                        title="Excluir"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>

                {/* Last used */}
                {entry.lastUsedAt && (
                  <div className="mt-2 text-xs text-muted-foreground">
                    Último uso: {new Date(entry.lastUsedAt).toLocaleString("pt-BR")}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      {/* Info */}
      <div className="bg-card border border-border rounded-xl p-4">
        <div className="flex items-start gap-3">
          <Lock className="w-5 h-5 text-primary mt-0.5" />
          <div className="text-sm text-muted-foreground space-y-1">
            <p className="font-medium text-foreground">Segurança do Vault</p>
            <p>Todas as credenciais são criptografadas com AES-256-GCM usando uma chave derivada por organização. Os valores nunca são exibidos em texto claro na interface — apenas uma versão mascarada parcial é mostrada.</p>
            <p>As credenciais do Vault são automaticamente disponibilizadas para os agentes YOS, tasks Manus e o chat durante a execução.</p>
          </div>
        </div>
      </div>
    </div>
  );
}
